import { clsx } from 'clsx';
import { Input } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { ChangeEvent, useEffect, useRef, useState } from 'react';

import { CxSurveyQuestionProps, OptionItemProps } from '@pages/admin-settings/Survey';
import cls from './SurveyMultipleOptions.module.css';


interface Props {
  question: CxSurveyQuestionProps;
  onOptionsChange: (itemIdx: string, options: OptionItemProps[]) => void;
}

interface OptionRowProps {
  item?: OptionItemProps;
  list: OptionItemProps[];
  onChange?: (idx: string, val: string) => void;
  onAdd?: (val: string) => void;
  onRemove?: (val: string) => void;
  className?: string;
}

const LIMIT = 10;

const OptionRow = ({ item, list, className, onAdd, onChange, onRemove }: OptionRowProps) => {
  const [name, setName] = useState(item ? item.label : '');
  const [error, setError] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    if (item && onChange) {
      onChange(item.idx, e.target.value);
    }
  };

  const handleAdd = () => {
    if (onAdd) {
      onAdd(name);
      setName('');
    }
  };

  useEffect(() => {
    if (hasDouble(list, name, item?.idx)) {
      setError(true);
    } else {
      setError(false);
    }
  }, [name]);

  return (
    <div className={clsx(cls.row, className)}>
      <div className={clsx(cls.col, cls.colName)}>
        <Input
          value={name}
          autoFocus
          className={clsx(cls.textField, error && cls.error)}
          bordered={false}
          placeholder={item ? undefined : 'Add new option'}
          onChange={handleChange} />
      </div>
      <div className={clsx(cls.col, cls.colActions)}>
        {!item && onAdd ? (
          <button className={clsx(cls.roundBtn, cls.addNewOptionBtn)} onClick={handleAdd}>
            <PlusOutlined className={cls.roundBtnIcon} />
          </button>
        ) : null}
        {item && onRemove ? (
          <button
            className={clsx(cls.roundBtn, cls.removeOptionBtn)}
            onClick={() => onRemove(item.idx)}>
            <PlusOutlined className={clsx(cls.roundBtnIcon, cls.addNewOptionBtnIcon)} />
          </button>
        ) : null}
      </div>
    </div>
  );
};

export const SurveyMultipleOptions = ({ question, onOptionsChange }: Props) => {
  const [list, setList] = useState<OptionItemProps[]>(question.options || []);
  const [show, setShow] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const onOptionAdd = (name: string) => {
    if (hasDouble(list, name) || !name.trim()) return;
    setList((prev) => [...prev, {
      idx: `${name.toLowerCase()}_${Date.now().toString()}`,
      value: null,
      label: name.trim(),
    }]);
  };

  const onOptionChange = (idx: string, val: string) => {
    if (hasDouble(list, val, idx)) return;
    setList((prev) => prev.map((o) => {
      return o.idx === idx ? { ...o, label: val.trim() } : o;
    }));
  };

  const onOptionRemove = (idx: string) => {
    setList((prev) => prev.filter((i) => i.idx !== idx));
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShow(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    onOptionsChange(question.idx, list);
  }, [list]);

  return (
    <div className={cls.wrap} ref={wrapperRef}>
      <button
        className={clsx(cls.roundBtn, cls.addOptionBtn, !list.length && cls.error)}
        onClick={() => setShow(!show)}>
        {list?.length ? <span className={cls.counter}>{list.length}</span> : null}
        <PlusOutlined className={clsx(cls.roundBtnIcon, cls.addOptionBtnIcon)} />
      </button>

      <div className={clsx(cls.options, show && cls.show)}>
        <div className={cls.arrow}>
          <svg className={cls.arrowIcon} width="16" height="12" viewBox="0 0 16 12" xmlns="http://www.w3.org/2000/svg">
            <polygon points="8,0 16,12 0,12" fill="white" />
            <line x1="8" y1="0" x2="0" y2="12" stroke="#042E6B" strokeWidth="1" />
            <line x1="8" y1="0" x2="16" y2="12" stroke="#042E6B" strokeWidth="1" />
          </svg>
        </div>
        {list.map((item) => (
          <OptionRow
            key={item.idx}
            onChange={onOptionChange}
            onRemove={onOptionRemove}
            list={list}
            item={item}
          />
        ))}
        {list.length < LIMIT
          ? <OptionRow
            list={list}
            onAdd={onOptionAdd}
            className={clsx(cls.newOption, list.length && cls.bordered)}
          />
          : null}
      </div>
    </div>
  );
};

function hasDouble (list: OptionItemProps[], val: string, idx?: string): boolean {
  return !!list.find(i => val
    && i.label.toLowerCase() === val.trim().toLowerCase() && i.idx !== idx);
}
